import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, CheckCircle, MessageSquare, Search, Shield, Phone } from 'lucide-react';

const tiers = [
  {
    step: 'Step 1',
    icon: MessageSquare,
    title: 'Ask a Tax Question - Free',
    subtitle: 'Free or Low-Cost Entry Point',
    description: 'Not every tax problem begins with full tax preparation. Sometimes you just need a clear answer from an experienced tax professional.',
    features: [
      'Submit your question online or by phone',
      'One-on-one guidance from a tax professional',
      'Clear next steps — no obligation',
      'Fixed fee or complimentary initial review',
    ],
    cta: 'Ask a Question',
    ctaPath: '/ask',
    highlight: false,
  },
  {
    step: 'Step 2',
    icon: Search,
    title: 'Tax Diagnostic Review',
    subtitle: 'Paid Diagnostic',
    description: 'If the issue is more serious, we perform a paid diagnostic review to examine your documents, explain your options, and recommend the next steps.',
    features: [
      'Full review of documents and transcripts',
      'Plain-English explanation of your situation',
      'Realistic options and recommendations',
      'Written summary of findings',
      'Scope and pricing for additional work',
    ],
    cta: 'Start Your Tax Review',
    ctaPath: '/book',
    highlight: true,
  },
  {
    step: 'Step 3',
    icon: Shield,
    title: 'Full-Service Engagement',
    subtitle: 'Resolution, Preparation, or Advisory',
    description: 'If your matter needs additional work, we provide tax preparation, tax resolution, and advisory services tailored to your situation.',
    features: [
      'IRS payment plans & penalty relief',
      'Unfiled and amended return preparation',
      'Notice response and collections help',
      'Small business and self-employed support',
      'Ongoing advisory and tax planning',
    ],
    cta: 'Book a Consultation',
    ctaPath: '/book',
    highlight: false,
  },
];

const faqs = [
  {
    question: 'What does the first step cost?',
    answer: 'The initial question or consultation is either free or a low fixed fee, depending on the complexity. We\'ll discuss this upfront before any commitment.',
  },
  {
    question: 'Do I have to commit to full service?',
    answer: 'No. Each step is independent. You can start with a question and stop there. The diagnostic is a separate engagement. Full service is offered only if needed and only if you choose it.',
  },
  {
    question: 'What happens after the first consultation?',
    answer: 'We\'ll give you a clear answer to your question and, if applicable, recommend next steps. If a deeper review is needed, we\'ll explain the diagnostic process and pricing.',
  },
  {
    question: 'How much does the diagnostic cost?',
    answer: 'The Tax Diagnostic Review is a fixed-fee service. The exact fee depends on the complexity of your situation and is discussed before we begin. No surprises.',
  },
  {
    question: 'Can I just get my taxes filed?',
    answer: 'Yes. Tax preparation is available as a standalone service. You don\'t need to go through the diagnostic first unless your situation involves IRS issues.',
  },
];

export default function Pricing() {
  useSEO({
    title: 'Pricing & How It Works',
    description: 'Simple steps, transparent pricing, no pressure. Start with a question, move to a paid diagnostic, get full-service help when needed.',
  });

  return (
    <div data-testid="pricing-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              Pricing / How It Works
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Simple Steps. Transparent Pricing. No Pressure.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              You decide how far to go. Start small, get answers, and only move forward when it makes sense for your situation.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Service Ladder */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {tiers.map((tier, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className={`p-8 h-full flex flex-col ${tier.highlight ? 'bg-brand-muted' : 'bg-white'}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-3">{tier.step}</p>
                  <div className="w-11 h-11 rounded-md bg-brand-navy/5 flex items-center justify-center mb-5">
                    <tier.icon className="w-5 h-5 text-brand-navy" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-navy mb-1">{tier.title}</h3>
                  <p className="text-sm font-medium text-slate-400 mb-4">{tier.subtitle}</p>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-6">{tier.description}</p>
                  <div className="space-y-3 mb-8 flex-1">
                    {tier.features.map((f, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={tier.ctaPath}>
                    <Button
                      data-testid={`pricing-cta-step-${i + 1}`}
                      className={`w-full font-semibold py-5 rounded-md ${
                        tier.highlight
                          ? 'bg-brand-green hover:bg-brand-green-hover text-white'
                          : 'bg-brand-navy hover:bg-brand-navy-light text-white'
                      }`}
                    >
                      {tier.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
                Frequently Asked Questions
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
                Common questions about pricing and process
              </h2>
              <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">
                We believe in transparency. If you have a question not answered here, call us at 855-589-2461 or submit a question.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="bg-white border border-slate-200 rounded-lg px-6 data-[state=open]:border-brand-green/30"
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="text-sm font-semibold text-brand-navy hover:no-underline py-4"
                    >
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Ready to take the first step?
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Start with a question. It's the simplest way to find out how we can help.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ask">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md">
                  Ask a Tax Question - Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> Call 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
